/**
 * โปรโมชั่น (Promos) — กฎส่วนลดที่บอตใช้คำนวณตอนลูกค้าสั่ง (ลดจากราคาใน "ราคา")
 * เชื่อมกับ: ราคา (Pricing) · ทดสอบบอต ("รับ 2 ชุด" → ใช้โปรไหน)
 *
 * ใช้ CrudManager กลาง — config/conditions เป็น JSON (field json validate ให้)
 */
import { CrudManager, type CrudField } from '@/components/CrudManager';
import { PromosApi, type PromoItem } from '@/lib/admin-api';
import { Badge } from '@/components/ui/badge';

const AUDIENCE = [
  { value: 'new', label: 'ลูกค้าใหม่ (ยังไม่เคยซื้อ)' },
  { value: 'returning', label: 'ลูกค้าเก่า (เคยซื้อแล้ว)' },
  { value: 'all', label: 'ทุกคน' },
];
const audLabel = (v: string) => AUDIENCE.find((a) => a.value === v)?.label ?? v;
const RULE_TYPES = [
  { value: 'flat_price_per_unit', label: 'ราคาคงที่ต่อหน่วย (เช่น 999/ชุด)' },
  { value: 'tier_percent_off', label: 'ลด % ตามจำนวน (ยิ่งซื้อมากยิ่งลด)' },
  { value: 'percent_off', label: 'ลด % คงที่' },
];
const ruleLabel = (v: string) => RULE_TYPES.find((r) => r.value === v)?.label ?? v;

const FIELDS: CrudField[] = [
  { key: 'ruleKey', label: 'รหัสโปร (key)', type: 'text', required: true, placeholder: 'new_999_per_set', disabledOnEdit: true, help: 'ตัวระบุภายใน ห้ามซ้ำ/เปลี่ยน' },
  { key: 'name', label: 'ชื่อโปร', type: 'text', required: true, placeholder: 'ลูกค้าใหม่ บิลแรก 999/ชุด', help: 'ชื่อที่บอตแสดงให้ลูกค้าเห็น' },
  { key: 'description', label: 'คำอธิบาย', type: 'textarea', help: 'รายละเอียดเพิ่มเติม (ไม่บังคับ)' },
  { key: 'audience', label: 'ใช้กับลูกค้า', type: 'select', required: true, options: AUDIENCE, default: 'new', help: 'โปรนี้ให้ใครได้บ้าง' },
  { key: 'ruleType', label: 'ประเภทส่วนลด', type: 'select', required: true, options: RULE_TYPES, default: 'flat_price_per_unit', help: 'รูปแบบการคิดส่วนลด' },
  { key: 'config', label: 'ค่าตั้งส่วนลด (JSON)', type: 'json', required: true, placeholder: '{ "pricePerUnit": 99900 }', help: 'ค่าของส่วนลด เช่น ราคาต่อชุด/เปอร์เซ็นต์ (สตางค์)' },
  { key: 'conditions', label: 'เงื่อนไข (JSON)', type: 'json', placeholder: '{ "minUnits": 2 }', help: 'เงื่อนไขเปิดใช้ เช่น ขั้นต่ำกี่ชุด (เว้นว่างได้)' },
  { key: 'priority', label: 'ลำดับความสำคัญ', type: 'number', default: 50, help: 'เลขสูง = ใช้ก่อนถ้าหลายโปรเข้าเกณฑ์' },
  { key: 'active', label: 'เปิดใช้งาน', type: 'toggle', default: true },
];

export default function PromosPage() {
  return (
    <CrudManager<PromoItem>
      title="🎁 โปรโมชั่น (Promos)"
      addLabel="เพิ่มโปร"
      help="กฎส่วนลดที่บอตคำนวณให้ลูกค้า (ลดจากราคาใน “ราคา”). priority สูง = เลือกก่อน. config/conditions เป็น JSON — ระบบตรวจให้"
      queryKey="promos"
      api={PromosApi}
      fields={FIELDS}
      brandScoped
      searchKeys={['ruleKey', 'name', 'audience']}
      sortRows={(a, b) => (b.priority ?? 0) - (a.priority ?? 0) || a.id - b.id}
      rowTitle={(p) => <>{p.name}</>}
      rowBadges={(p) => (
        <>
          <Badge variant="outline" className="text-[10px]">{audLabel(p.audience)}</Badge>
        </>
      )}
      rowSubtitle={(p) => <>{ruleLabel(p.ruleType)} · <code className="text-[11px]">{p.ruleKey}</code>{p.description ? ` · ${p.description}` : ''}</>}
      renderRowExtra={(p) => (
        <div className="text-xs space-y-1">
          <div className="font-medium">ค่าตั้ง (config):</div>
          <pre className="bg-muted/40 rounded p-2 overflow-auto">{JSON.stringify(p.config, null, 2)}</pre>
          {p.conditions && (<><div className="font-medium">เงื่อนไข:</div><pre className="bg-muted/40 rounded p-2 overflow-auto">{JSON.stringify(p.conditions, null, 2)}</pre></>)}
        </div>
      )}
    />
  );
}
